/**
 * ``NumberStepper`` — a small numeric input with decrement / increment
 * buttons, clamped to ``[min, max]`` by ``step`` (issue #278 follow-up,
 * PR1). The map settings modal uses it for the dynamic-track length (km).
 *
 * Controlled: the parent owns ``value`` and persists ``onChange``. Values
 * are clamped to the range on every commit (button or typed). No new
 * dependency — plain input + Phosphor +/- icons (matching the app's icon
 * set).
 */
import { Minus, Plus } from "@phosphor-icons/react";

export interface NumberStepperProps {
  /** Current value (controlled). */
  value: number;
  /** Called with the clamped next value on a button press or a valid edit. */
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  step?: number;
  /** Bound to the input for label association. */
  id?: string;
  /** Accessible label for the input. */
  ariaLabel?: string;
  /** Unit suffix shown after the input (e.g. "km"). */
  unitLabel?: string;
}

export function NumberStepper({
  value,
  onChange,
  min = 0,
  max = Number.MAX_SAFE_INTEGER,
  step = 1,
  id,
  ariaLabel,
  unitLabel,
}: NumberStepperProps): React.ReactElement {
  const clamp = (n: number): number => Math.min(max, Math.max(min, n));
  const commit = (n: number): void => {
    if (!Number.isNaN(n)) {
      onChange(clamp(n));
    }
  };

  const buttonClass =
    "flex h-7 w-7 items-center justify-center rounded border border-border " +
    "text-text-secondary hover:bg-bg-elevated hover:text-text-primary " +
    "disabled:cursor-not-allowed disabled:opacity-40";

  return (
    <div className="flex items-center gap-2" data-testid="number-stepper">
      <button
        type="button"
        aria-label="Decrease"
        disabled={value <= min}
        onClick={() => commit(value - step)}
        className={buttonClass}
      >
        <Minus size={14} />
      </button>
      <input
        type="number"
        id={id}
        aria-label={ariaLabel}
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => {
          // Empty / partial input -> don't commit (keep the last valid value);
          // a parseable number commits clamped.
          if (e.target.value !== "") {
            commit(Number(e.target.value));
          }
        }}
        data-testid="number-stepper-input"
        className={[
          "w-16 rounded border border-border bg-bg-surface px-2 py-1 text-center",
          "text-body text-text-primary",
          "focus:outline-none focus-visible:ring-1 focus-visible:ring-brand",
        ].join(" ")}
      />
      {unitLabel !== undefined && (
        <span className="text-body-sm text-text-secondary">{unitLabel}</span>
      )}
      <button
        type="button"
        aria-label="Increase"
        disabled={value >= max}
        onClick={() => commit(value + step)}
        className={buttonClass}
      >
        <Plus size={14} />
      </button>
    </div>
  );
}
